/**
 * Ships Management Page
 * =====================
 * صفحة إدارة الأشياب
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import { useDialog } from '../../../context/DialogContext';

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  ship: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20h20M4 20V10l8-6 8 6v10"/><path d="M9 20v-6h6v6"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  tank: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 20V10a6 6 0 1 1 12 0v10"/><path d="M4 20h16"/><path d="M12 4v2"/></svg>,
  orders: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
};

// دالة لحساب حالة مفتوح/مغلق
const getOpenStatus = (ship) => {
  if (ship.is_24_hours) return { isOpen: true, text: 'مفتوح 24 ساعة', color: '#10b981' };
  
  if (!ship.opening_time || !ship.closing_time) return { isOpen: false, text: 'غير محدد', color: '#6b7280' };
  
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  
  const [openH, openM] = ship.opening_time.split(':').map(Number);
  const [closeH, closeM] = ship.closing_time.split(':').map(Number);
  
  const openTime = openH * 60 + openM;
  const closeTime = closeH * 60 + closeM;
  
  // إذا كان وقت الإغلاق أصغر من وقت الفتح، يعني يمتد لليوم التالي
  let isOpen;
  if (closeTime < openTime) {
    isOpen = currentTime >= openTime || currentTime < closeTime;
  } else {
    isOpen = currentTime >= openTime && currentTime < closeTime;
  }
  
  return {
    isOpen,
    text: isOpen ? 'مفتوح الآن' : 'مغلق',
    color: isOpen ? '#10b981' : '#ef4444'
  };
};

const ShipsPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [ships, setShips] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingShip, setEditingShip] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_ar: '', address: '', city: '', capacity_daily_tons: '', contact_phone: '' });

  const loadShips = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('status', filter);
      const data = await apiCall(`/tenant/ships?${params}`);
      setShips(data.items || []);
    } catch (err) {
      showError('فشل تحميل الأشياب');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, filter, showError]);

  useEffect(() => { loadShips(); }, [loadShips]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingShip) {
        await apiCall(`/tenant/ships/${editingShip.id}`, { method: 'PUT', body: JSON.stringify(formData) });
        success('تم تحديث الشيب بنجاح');
      } else {
        await apiCall('/tenant/ships', { method: 'POST', body: JSON.stringify(formData) });
        success('تم إضافة الشيب بنجاح');
      }
      setShowModal(false);
      setEditingShip(null);
      setFormData({ name: '', name_ar: '', address: '', city: '', capacity_daily_tons: '', contact_phone: '' });
      loadShips();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const handleEdit = (ship) => {
    setEditingShip(ship);
    setFormData({ name: ship.name || '', name_ar: ship.name_ar || '', address: ship.address || '', city: ship.city || '', capacity_daily_tons: ship.capacity_daily_tons || '', contact_phone: ship.contact_phone || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'إغلاق الشيب', message: 'هل أنت متأكد من إغلاق هذا الشيب؟', type: 'warning', confirmText: 'إغلاق', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/ships/${id}`, { method: 'DELETE' });
      success('تم إغلاق الشيب بنجاح');
      loadShips();
    } catch (err) {
      showError('فشل إغلاق الشيب');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة الأشياب</h1>
          <p>{ships.length} شيب</p>
        </div>
        <button className="btn btn--primary" onClick={() => navigate('/tenant/ships/add')}>
          {I.plus} <span>إضافة شيب</span>
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          {I.search}
          <input type="text" placeholder="بحث بالاسم أو الكود..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">جميع الحالات</option>
          <option value="active">نشط</option>
          <option value="maintenance">صيانة</option>
          <option value="closed">مغلق</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : ships.length === 0 ? (
        <div className="page-empty">{I.ship}<h3>لا يوجد أشياب</h3><p>ابدأ بإضافة أشياب جديدة</p></div>
      ) : (
        <div className="ships-grid">
          {ships.map(ship => {
            const openStatus = getOpenStatus(ship);
            return (
              <div 
                key={ship.id} 
                className="ship-card"
                onClick={() => navigate(`/tenant/ships/${ship.id}`)}
              >
                {/* Header */}
                <div className="ship-card__header">
                  <div className="ship-card__info">
                    <h3 className="ship-card__name">{ship.name_ar || ship.name}</h3>
                    <span className="ship-card__code">{ship.code}</span>
                  </div>
                  <span className={`ship-card__status ship-card__status--${ship.status || 'active'}`}>
                    {ship.status === 'maintenance' ? 'صيانة' : ship.status === 'closed' ? 'مغلق' : 'نشط'}
                  </span>
                </div>

                {/* Location */}
                <div className="ship-card__location">
                  <span className="ship-card__icon">{I.map}</span>
                  <span>{ship.city || ship.district || ship.address || 'غير محدد'}</span>
                </div>

                {/* Open Status */}
                <div className="ship-card__open-status" style={{ color: openStatus.color }}>
                  <span className="ship-card__icon">{I.clock}</span>
                  <span>{openStatus.text}</span>
                </div>

                {/* Stats */}
                <div className="ship-card__stats">
                  <div className="ship-card__stat">
                    <span className="ship-card__stat-icon" style={{ color: '#f59e0b' }}>{I.star}</span>
                    <span className="ship-card__stat-value">{ship.rating_avg?.toFixed(1) || '0.0'}</span>
                    <span className="ship-card__stat-label">({ship.total_orders || 0} طلب)</span>
                  </div>
                  <div className="ship-card__stat">
                    <span className="ship-card__stat-icon" style={{ color: 'var(--tenant-primary)' }}>{I.tank}</span>
                    <span className="ship-card__stat-value">{ship.current_stock_tons || 0}</span>
                    <span className="ship-card__stat-label">/ {ship.capacity_daily_tons || 0} طن</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="ship-card__footer">
                  <div className="ship-card__actions">
                    <button 
                      className="ship-card__action-btn" 
                      onClick={(e) => { e.stopPropagation(); navigate(`/tenant/ships/edit/${ship.id}`); }}
                      title="تعديل"
                    >
                      {I.edit}
                    </button>
                    <button 
                      className="ship-card__action-btn ship-card__action-btn--danger" 
                      onClick={(e) => { e.stopPropagation(); handleDelete(ship.id); }}
                      title="حذف"
                    >
                      {I.trash}
                    </button>
                  </div>
                  <div className="ship-card__view">
                    <span>عرض التفاصيل</span>
                    {I.arrow}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingShip ? 'تعديل شيب' : 'إضافة شيب جديد'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group"><label>الاسم (إنجليزي) *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                  <div className="form-group"><label>الاسم (عربي)</label><input type="text" value={formData.name_ar} onChange={(e) => setFormData({...formData, name_ar: e.target.value})} /></div>
                  <div className="form-group"><label>المدينة</label><input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} /></div>
                  <div className="form-group"><label>السعة اليومية (طن)</label><input type="number" value={formData.capacity_daily_tons} onChange={(e) => setFormData({...formData, capacity_daily_tons: e.target.value})} /></div>
                  <div className="form-group full-width"><label>العنوان</label><input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></div>
                  <div className="form-group full-width"><label>رقم التواصل</label><input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">{editingShip ? 'حفظ التعديلات' : 'إضافة'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .ships-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }
        
        .ship-card {
          background: var(--tenant-card-bg);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid var(--tenant-border);
        }
        
        .ship-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
          border-color: var(--tenant-primary);
        }
        
        .ship-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        
        .ship-card__info {
          flex: 1;
        }
        
        .ship-card__name {
          font-size: 18px;
          font-weight: 600;
          color: var(--tenant-text);
          margin: 0 0 4px;
        }
        
        .ship-card__code {
          font-size: 12px;
          color: var(--tenant-text-muted);
          font-family: monospace;
          background: var(--tenant-bg);
          padding: 2px 8px;
          border-radius: 4px;
        }
        
        .ship-card__status {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .ship-card__status--active {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        
        .ship-card__status--maintenance {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }
        
        .ship-card__status--closed {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        
        .ship-card__location,
        .ship-card__open-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--tenant-text-muted);
          margin-bottom: 8px;
        }
        
        .ship-card__icon {
          display: flex;
          align-items: center;
        }
        
        .ship-card__icon svg {
          width: 16px;
          height: 16px;
        }
        
        .ship-card__stats {
          display: flex;
          gap: 20px;
          padding: 16px 0;
          margin: 12px 0;
          border-top: 1px solid var(--tenant-border);
          border-bottom: 1px solid var(--tenant-border);
        }
        
        .ship-card__stat {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .ship-card__stat-icon svg {
          width: 18px;
          height: 18px;
        }
        
        .ship-card__stat-value {
          font-size: 16px;
          font-weight: 600;
          color: var(--tenant-text);
        }
        
        .ship-card__stat-label {
          font-size: 12px;
          color: var(--tenant-text-muted);
        }
        
        .ship-card__footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
        }
        
        .ship-card__actions {
          display: flex;
          gap: 8px;
        }
        
        .ship-card__action-btn {
          width: 36px;
          height: 36px;
          border: none;
          background: var(--tenant-bg);
          border-radius: 8px;
          cursor: pointer;
          color: var(--tenant-text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .ship-card__action-btn:hover {
          background: var(--tenant-primary);
          color: white;
        }
        
        .ship-card__action-btn--danger:hover {
          background: #ef4444;
        }
        
        .ship-card__action-btn svg {
          width: 16px;
          height: 16px;
        }
        
        .ship-card__view {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--tenant-primary);
          font-weight: 500;
        }
        
        .ship-card__view svg {
          width: 16px;
          height: 16px;
        }
        
        @media (max-width: 768px) {
          .ships-grid {
            grid-template-columns: 1fr;
          }
          
          .ship-card__stats {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default ShipsPage;
